import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import classnames from 'classnames';
import { Drawer, Checkbox, FormControlLabel } from '@material-ui/core';
import { connect } from 'react-redux';
import lodash from 'lodash';
import moment from 'moment';
import uuid from 'uuid';
import DateTimePicker from 'common/components/DateTime/DateTimePicker';
import { getDateNoTime } from 'common/util/dateTime';
import styles from './CreateDelegationPanel.scss';

import { Conditional } from 'common/components/layout';
import { Spinner } from 'components/common/LoadingStatus/LoadingStatus';
import { Button, BUTTON } from 'components/common/Button/Button';
import { customStyles } from 'components/common/Dropdown/DropDownListHelper';
import MultiSelectCheckbox from 'components/common/MultiSelectCheckbox/MultiSelectCheckbox';
import MultiSelect from 'components/common/MultiSelect/MultiSelect';
import { addStudyDelegationV2, stackAnErrorMessage } from 'pages/delegationOfAuthority/v2/actions/studyDelegationsV2';
import {
    delegationsList,
    getDelegationAvailableUserOptions,
    getDelegationTaskOptions,
    getDelegationRoleOptions,
} from 'pages/delegationOfAuthority/v2/selectors';
import {
    delegationsUnderPITenurePredicate,
} from 'pages/delegationOfAuthority/v2/util/predicates';
import { verifyNoPITenuresOverlapped } from 'pages/delegationOfAuthority/v2/util/validators';
import DelegationStatement from 'pages/delegationOfAuthority/v2/components/DelegationStatement/DelegationStatement';
import { isActivePI } from 'pages/delegationOfAuthority/util/predicates';

const SELECT_STYLES = lodash.merge({}, customStyles, {
    control: (provided) => ({
        ...provided,
        backgroundColor: 'transparent',
    }),
    placeholder: (provided) => ({
        ...provided,
        color: 'rgba(91, 91, 93, 1.0)',
    }),
});

const PI_ROLE = 'Principal Investigator';

function CreateDelegationPanel({ open, onClose, institutionTimezone, ...props }) {
    const [staffSelected, setStaffSelected] = useState(null);
    const [roleSelected, setRoleSelected] = useState(null);
    const [tasksSelected, setTasksSelected] = useState([]);
    const [startDateSelected, setStartDateSelected] = useState(null);
    const [endDateSelected, setEndDateSelected] = useState(null);
    const [currentCalendarStart, setCurrentCalendarStart] = useState(undefined);
    const [currentCalendarEnd, setCurrentCalendarEnd] = useState(undefined);

    const [enableSignatureSuggestion, toggleSignatureSuggestion] = useState(false);
    const [suggestedSignatures, setSuggestedSignatures] = useState([]);
    const [signaturesSelected, setSignaturesSelected] = useState([]);

    const [delegationRequiringNewPISignature, setDelegationRequiringNewPiSignature] = useState([]);

    const [errorSections, setErrorSections] = useState([]);
    const tenureSelectRef = useRef(null);

    useEffect(() => {
        return () => clearForm();
    }, [open]);

    useEffect(() => {
        const signatureSuggestionEnabled = !(
            lodash.isNil(staffSelected) ||
            lodash.isNil(roleSelected) ||
            lodash.isEmpty(tasksSelected) ||
            lodash.isNil(startDateSelected)
        );

        toggleSignatureSuggestion(signatureSuggestionEnabled);
        signatureSuggestionEnabled ? suggestSignatures() : clearSuggestedSignatures();

        const existingDoAEnabled =
            !(lodash.isNil(staffSelected) || lodash.isNil(roleSelected) || lodash.isNil(startDateSelected)) &&
            lodash.get(roleSelected, 'label') === PI_ROLE;
        existingDoAEnabled ? collectExistingDelegations() : clearExistingDelegations();
    }, [staffSelected, roleSelected, tasksSelected, startDateSelected, endDateSelected]);

    const clearForm = () => {
        setStaffSelected(null);
        setRoleSelected(null);
        setTasksSelected([]);
        setStartDateSelected(null);
        setEndDateSelected(null);
    };

    const parseDateFormat = (date) => {
        if (lodash.isNil(date)) {
            return null;
        }

        const dt = moment(date).format('LL');
        return moment.utc(dt);
    };

    const onStartDateChange = (selected) => {
        const startDayOfSelected = selected ? moment(selected).startOf('day').toDate() : null;
        setStartDateSelected(parseDateFormat(selected));
        setCurrentCalendarEnd(selected);

        if (lodash.isNil(startDayOfSelected)) {
            setEndDateSelected(null);
        }
    };

    const onEndDateChange = (selected) => {
        setEndDateSelected(parseDateFormat(selected));
        setCurrentCalendarStart(selected);
    };

    const onRemoveSignatureIndicator = (removed) => {
        setSignaturesSelected((signatures) => lodash.filter(signatures, (signature) => removed !== signature.value));
    };

    const onToggleNewPiSignatureForExistingDelegation = (target) => {
        const targetDelegation = lodash.find(delegationRequiringNewPISignature, ({ id }) => target === id);
        targetDelegation.checked = !targetDelegation.checked;
        setDelegationRequiringNewPiSignature(lodash.cloneDeep(delegationRequiringNewPISignature));
    };

    const collectPISignatures = () => {
        if (lodash.get(roleSelected, 'label') === PI_ROLE) {
            return [
                {
                    label: staffSelected.label,
                    value: `${staffSelected.value}::P.I.`,
                    data: { userId: staffSelected.value, role: 'P.I.' },
                },
            ];
        }

        const { currentDOA } = props;

        return lodash
            .chain(currentDOA)
            .filter(
                ({ role, startDate, endDate }) =>
                    lodash.get(role, 'name') === PI_ROLE &&
                    isActivePI([startDate, endDate], parseDateFormat(moment()))
            )
            .map(({ user }) => ({
                label: user.displayName,
                value: `${user.id}::P.I.`,
                data: {
                    role: 'P.I.',
                    userId: user.id,
                },
            }))
            .uniqBy('value')
            .value();
    };

    const collectStaffSignatures = () => {
        const { doaMode } = props;
        if (doaMode === 'CENTRAL' || lodash.get(roleSelected, 'label') === PI_ROLE) {
            return [];
        }

        return [
            {
                label: staffSelected.label,
                value: `${staffSelected.value}::Staff`,
                data: { userId: staffSelected.value, role: 'Staff' },
            },
        ];
    };

    const collectExistingDelegations = () => {
        const { currentDOA } = props;
        const staffDelegationsOverlappingWithNewPITenure = delegationsUnderPITenurePredicate(currentDOA, [
            startDateSelected,
            endDateSelected,
        ]);

        const delegationOptions = lodash.map(staffDelegationsOverlappingWithNewPITenure, (delegation) => ({
            id: lodash.get(delegation, 'id'),
            checked: true,
            label: lodash.get(delegation, 'user.displayName'),
            data: delegation,
        }));

        setDelegationRequiringNewPiSignature(delegationOptions);
    };

    const suggestSignatures = () => {
        const signatures = [];
        const piSignatureSuggested = collectPISignatures();
        const staffSignatureSuggested = collectStaffSignatures();

        signatures.push(...piSignatureSuggested);
        signatures.push(...staffSignatureSuggested);

        setSuggestedSignatures(signatures);
        setSignaturesSelected(signatures);
    };

    const clearSuggestedSignatures = () => {
        setSuggestedSignatures([]);
        setSignaturesSelected([]);
    };

    const clearExistingDelegations = () => {
        setDelegationRequiringNewPiSignature([]);
    };

    const scrollIntoView = (ref) => {
        if (lodash.isNil(ref.current)) {
            return;
        }

        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const markErrors = (errors, duration = 3000) => {
        setErrorSections(errors);
        setTimeout(() => setErrorSections([]), duration);
    };

    const validate = () => {
        const { currentDOA, stackAnErrorMessage } = props;
        const errors = [];

        verifyNoPITenuresOverlapped(
            currentDOA,
            lodash.get(roleSelected, 'label'),
            [startDateSelected, endDateSelected],
            () => errors.push('StartDate', 'EndDate')
        );

        if (lodash.isEmpty(errors)) {
            return true;
        }

        stackAnErrorMessage(
            'This PI can\'t be added since their tenure conflicts with an existing PI\'s tenure. ' +
            'If you want to add this PI, their tenure will need to be modified.'
        );
        scrollIntoView(tenureSelectRef);
        markErrors(errors);
        return false;
    };

    const onSubmit = () => {
        if (!validate()) {
            return;
        }

        const { binderId, addStudyDelegationV2 } = props;
        const signatureExpectations = [];

        const delegation = lodash.cloneDeep({
            binderId,
            id: uuid.v4(),
            user: {
                id: lodash.get(staffSelected, 'user.id'),
                name: lodash.get(staffSelected, 'user.displayName'),
            },
            roleManagementId: lodash.get(roleSelected, 'role.id', null),
            tasks: lodash.map(tasksSelected, (option) => lodash.get(option, 'task')),
            startDate: moment(startDateSelected).isValid() ? moment(startDateSelected).toISOString() : null,
            endDate: moment(endDateSelected).isValid() ? moment(endDateSelected).toISOString() : null,
        });

        if (!lodash.isEmpty(signaturesSelected)) {
            const expectations = lodash.map(signaturesSelected, (option) => ({
                delegationId: delegation.id,
                user: {
                    id: lodash.get(option, 'data.userId'),
                    name: lodash.get(option, 'label'),
                },
                signatureType: lodash.get(option, 'data.role') === 'P.I.' ? 'PI_START' : 'STAFF_START',
            }));

            signatureExpectations.push(...expectations);
        }

        if (!lodash.isEmpty(delegationRequiringNewPISignature)) {
            const signaturesForExistingDelegation = lodash
                .chain(delegationRequiringNewPISignature)
                .filter(({ checked }) => checked)
                .map((option) => ({
                    delegationId: lodash.get(option, 'data.id'),
                    user: {
                        id: lodash.get(staffSelected, 'user.id'),
                        name: lodash.get(staffSelected, 'user.displayName'),
                    },
                    signatureType: 'PI_START',
                }))
                .value();
            signatureExpectations.push(...signaturesForExistingDelegation);
        }

        const defaultSignatureExpectations = lodash.map(suggestedSignatures, (option) => ({
            delegationId: delegation.id,
            user: {
                id: lodash.get(option, 'data.userId'),
                name: lodash.get(option, 'label'),
            },
            signatureType: lodash.get(option, 'data.role') === 'P.I.' ? 'PI' : 'STAFF',
        }));
        addStudyDelegationV2(binderId, delegation, signatureExpectations, defaultSignatureExpectations, () => onClose());
    };

    const renderInputGroup = () => (
        <section className={styles.DOAInformationGroup}>
            <InputSection label="Staff Member" span="2" required>
                <Select
                    name="staff-member"
                    placeholder="Select Staff Member"
                    options={props.staffOptions}
                    styles={SELECT_STYLES}
                    value={staffSelected}
                    onChange={(selected) => setStaffSelected(selected)}
                />
            </InputSection>

            <InputSection label="Role" span="2">
                <Select
                    name="role"
                    placeholder="Select Role"
                    options={props.roleOptions}
                    styles={SELECT_STYLES}
                    value={roleSelected}
                    onChange={(selected) => setRoleSelected(selected)}
                />
            </InputSection>

            <InputSection label="Tasks" span="2">
                <MultiSelect
                    className={styles.MultiSelectCheckbox}
                    options={props.taskOptions}
                    selectedOptions={tasksSelected}
                    onChanged={(selected) => setTasksSelected(selected)}
                    renderValueIndicator={TaskSelectValueIndicator}
                    placeholder="Select Study Tasks"
                />
            </InputSection>

            <InputSection label="Start Date" innerRef={tenureSelectRef} error={errorSections.includes('StartDate')}>
                <DateTimePicker
                    className={styles.DateTimePicker}
                    autoFocus={false}
                    time={false}
                    value={startDateSelected}
                    onChange={onStartDateChange}
                    currentDate={currentCalendarStart}
                    onCurrentDateChange={(current) => setCurrentCalendarStart(current)}
                    max={getDateNoTime(endDateSelected) || getDateNoTime(new Date(2099, 11, 31))}
                />
            </InputSection>

            <InputSection label="End Date" error={errorSections.includes('EndDate')}>
                <DateTimePicker
                    className={styles.DateTimePicker}
                    autoFocus={false}
                    time={false}
                    value={endDateSelected}
                    min={getDateNoTime(startDateSelected)}
                    onChange={onEndDateChange}
                    currentDate={currentCalendarEnd}
                    onCurrentDateChange={(current) => setCurrentCalendarEnd(current)}
                    disabled={lodash.isNil(startDateSelected)}
                />
            </InputSection>
        </section>
    );

    const renderSignatureExpectation = () => {
        const groupedOptions = lodash.reduce(
            suggestedSignatures,
            (output, option) => {
                const targetRole = lodash.get(option, 'data.role', 'Staff');
                const targetGroup = lodash.find(output, (group) => group.label === targetRole);
                targetGroup.options.push(option);

                return output;
            },
            [
                { label: 'P.I.', options: [] },
                { label: 'Staff', options: [] },
            ]
        );

        const numberOfSuggestedSignatures = lodash.get(suggestedSignatures, 'length');
        const numberOfSelectedSignatures = lodash.get(signaturesSelected, 'length');

        let helpText = 'Signatures may not be requested until a role, a task, and a start date are provided.';

        if (enableSignatureSuggestion) {
            helpText =
                numberOfSuggestedSignatures === 0
                    ? 'Signature requests haven’t been recommended since this delegation might not overlap with the PI’s tenure or the PI delegation has not been added yet.'
                    : `${numberOfSuggestedSignatures} signature request${numberOfSuggestedSignatures === 1 ? '' : 's'} recommended. ` +
                      'You can remove or modify this to ensure signature ordering, or if you are unsure of the current delegation details.';
        }

        return (
            <section className={styles.DOASignatureExpectation}>
                <div className={styles.DOASignatureRequest}>
                    <h5>Signature Requests</h5>
                    <DelegationStatement type="warning" size="small">
                        <span>{helpText}</span>
                    </DelegationStatement>

                    <Conditional condition={numberOfSuggestedSignatures !== 0}>
                        <InputSection
                            label={`${numberOfSelectedSignatures} Signature Request${numberOfSelectedSignatures === 1 ? '' : 's'}`}
                        >
                            <MultiSelectCheckbox
                                className={styles.MultiSelectCheckbox}
                                options={groupedOptions}
                                selectedOptions={signaturesSelected}
                                onChanged={(selected) => setSignaturesSelected(selected)}
                                renderValueIndicator={SignatureSelectValueIndicator}
                                placeholder="Select Staff for Signatures"
                                searchBarPlaceholder="Search for Users"
                                isSelectAllRequired={false}
                            />
                            <SignatureSelectedIndicator
                                signatures={signaturesSelected}
                                onClose={onRemoveSignatureIndicator}
                            />
                        </InputSection>
                    </Conditional>
                </div>
                <div className={styles.DOACompletedSignatures}>
                    <h5>Completed Signatures</h5>
                    <p>0 Completed Signatures</p>
                </div>
            </section>
        );
    };

    const renderNewPISignatureForExistingDelegation = () => {
        const numberOfSelected = lodash.filter(delegationRequiringNewPISignature, ({ checked }) => checked).length;
        const singularOrPlural = numberOfSelected === 1 ? '' : 's';
        return (
            <Conditional condition={lodash.get(delegationRequiringNewPISignature, 'length') !== 0}>
                <section className={styles.NewPISignatureForExistingDelegation}>
                    <h5>Delegations that Require New P.I. Signature</h5>
                    <DelegationStatement type="warning" size="small">
                        <span>
                            Signature requests are automatically generated for any delegations that will be under the
                            tenure of the new PI. You can deselect delegations that don’t require a new P.I. signature.
                        </span>
                    </DelegationStatement>
                    <InputSection label={`${numberOfSelected} Delegation${singularOrPlural} Selected`}>
                        {lodash.map(delegationRequiringNewPISignature, ({ id, label, checked }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        disableRipple
                                        color="primary"
                                        checked={checked}
                                        onChange={() => onToggleNewPiSignatureForExistingDelegation(id)}
                                    />
                                }
                                label={label}
                            />
                        ))}
                    </InputSection>
                </section>
            </Conditional>
        );
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            hideBackdrop
            disableEscapeKeyDown
            classes={{
                root: styles.CreateDelegationPanel,
            }}
        >
            <div className={styles.Container}>
                <Header title="New Delegation" onClosePanel={onClose} />

                <CreateDelegationForm>
                    {renderInputGroup()}
                    {renderSignatureExpectation()}
                    {renderNewPISignatureForExistingDelegation()}
                </CreateDelegationForm>

                <LoadingOverlay loading={lodash.get(props, 'onAddingDelegation')} />

                <Footer
                    onRequesting={lodash.get(props, 'onAddingDelegation')}
                    onAddDelegation={onSubmit}
                    onClosePanel={onClose}
                    unableToSubmit={lodash.isNil(staffSelected)}
                />
            </div>
        </Drawer>
    );
}

export function InputSection({ label, span, innerRef, required, error, children }) {
    return (
        <div className={styles.InputSection} span={span} ref={innerRef}>
            <label
                className={classnames(styles.InputSection__label, {
                    [styles.InputSection__required]: required,
                    [styles.InputSection__error]: error,
                })}
            >
                {label}
            </label>
            {children}
        </div>
    );
}

export function CreateDelegationForm({ children }) {
    return <div className={styles.CreateDelegationForm}>{children}</div>;
}

export function DOAInformationGroup({ children }) {
    return <div className={styles.DOAInformationGroup}>{children}</div>;
}

export function Header({ title, onClosePanel }) {
    return (
        <div className={styles.Header}>
            <h5>{title}</h5>
            <i className={styles.CloseIcon} onClick={onClosePanel}>
                close
            </i>
        </div>
    );
}

export function Footer({ onClosePanel, onAddDelegation, unableToSubmit, onRequesting }) {
    return (
        <div className={styles.Footer}>
            <Button
                bgcolor={BUTTON.BGCOLOR.CLEAR}
                color={BUTTON.COLOR.BLACK}
                outline={BUTTON.OUTCOLOR.LIGHTGREY}
                onClick={onClosePanel}
                disabled={onRequesting}
            >
                Cancel
            </Button>
            <Button
                bgcolor={BUTTON.BGCOLOR.ORANGE}
                color={BUTTON.COLOR.WHITE}
                onClick={onAddDelegation}
                disabled={unableToSubmit || onRequesting}
            >
                {onRequesting ? 'Adding..' : 'Add Delegation'}
            </Button>
        </div>
    );
}

export function LoadingOverlay({ loading = false }) {
    return (
        <Conditional condition={loading}>
            <div className={styles.LoadingOverlay}>
                <Spinner actionLabel={'Adding Delegation'} />
            </div>
        </Conditional>
    );
}

function TaskSelectValueIndicator({ placeholderButtonLabel, value }) {
    if (!value) {
        return <span className={styles.MultiSelectCheckbox__placeholder}>{placeholderButtonLabel}</span>;
    }
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return <span className={styles.MultiSelectCheckbox__placeholder}>{placeholderButtonLabel}</span>;
        }
        return `${value.length} Study Task${value.length === 1 ? '' : 's'}`;
    }
    return value.label;
}

function SignatureSelectValueIndicator({ placeholderButtonLabel, value }) {
    if (!value) {
        return <span className={styles.MultiSelectCheckbox__placeholder}>{placeholderButtonLabel}</span>;
    }
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return <span className={styles.MultiSelectCheckbox__placeholder}>{placeholderButtonLabel}</span>;
        }
        return `${value.length} Selected`;
    }
    return value.label;
}

function SignatureSelectedIndicator({ signatures, onClose }) {
    return (
        <React.Fragment>
            {lodash.map(signatures, ({ label, data, value }) => (
                <div key={lodash.get(data, 'id')} className={styles.SignatureSelectedIndicator}>
                    <div className={styles.SignatureSelectedIndicator__entry}>
                        <span className={styles.SignatureSelectedIndicator__label}>{label}</span>
                        <span className={styles.SignatureSelectedIndicator__role}>{lodash.get(data, 'role')}</span>
                    </div>
                    <i className={styles.CloseIcon} onClick={() => onClose(value)}>
                        close
                    </i>
                </div>
            ))}
        </React.Fragment>
    );
}

export default connect(
    (state, props) => {
        const binderId = lodash.get(state, 'binder.id');
        const me = lodash.get(state, 'me', {});
        const staffOptions = getDelegationAvailableUserOptions(state);
        const taskOptions = getDelegationTaskOptions(state);
        const currentDOA = delegationsList(state);
        const roleOptions = getDelegationRoleOptions(state);
        const doaMode = lodash.get(me, 'isInstitutionCentralized') === 'true' ? 'CENTRAL' : 'STANDARD';
        const onAddingDelegation = lodash.get(state, 'app.upsertDelegationRequesting', false);
        const institutionTimezone = lodash.get(me, 'institutionTimezone', 'Z');

        return {
            binderId,
            me,
            staffOptions,
            taskOptions,
            currentDOA,
            roleOptions,
            doaMode,
            onAddingDelegation,
            institutionTimezone,
        };
    },
    { addStudyDelegationV2, stackAnErrorMessage }
)(CreateDelegationPanel);
